import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { 
  Upload, 
  FileSpreadsheet, 
  CheckCircle2, 
  AlertCircle,
  Download,
  Sparkles
} from "lucide-react";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { LeadLimitModal } from "@/components/modals/LeadLimitModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ImportLeads = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  
  const { handleApiError, modals, closeModal } = useUsageLimits();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.csv')) {
        setFile(droppedFile);
      } else {
        toast.error("Please upload a CSV file");
      }
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.csv')) {
        setFile(selectedFile);
      } else {
        toast.error("Please upload a CSV file");
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/leads/import`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setResult(response.data);
      toast.success(`Successfully imported ${response.data.imported} leads!`);
      
      // Wait a bit then redirect
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (error) {
      console.error("Upload error:", error);
      // Try to handle as usage limit error
      try {
        handleApiError(error);
      } catch (e) {
        // Not a usage limit error, handle normally
        toast.error(error.response?.data?.detail || "Failed to import leads");
      }
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const template = "name,email,phone,company,title,website,lead_notes\nJohn Doe,john@example.com,+1234567890,Acme Inc,Sales Manager,https://acme.com,Interested in our product\nJane Smith,jane@techcorp.com,+0987654321,TechCorp,Marketing Director,https://techcorp.com,Follow up in Q2";
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Template downloaded!");
  };

  return (
    <div className="space-y-6 fade-in" data-testid="import-leads-container">
      {/* Header */}
      <div>
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{fontFamily: 'Space Grotesk, sans-serif'}}>Import Leads</h2>
        <p className="text-sm sm:text-base text-slate-600 mt-1">Upload a CSV file to bulk import and enrich your leads</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Upload Section */}
        <div className="lg:col-span-2">
          <Card className="glass-card border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Upload className="w-5 h-5 text-indigo-600" />
                <span>Upload CSV File</span>
              </CardTitle>
              <CardDescription>
                Upload a CSV file with lead information. The file should include name, email, phone, company, and title columns.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Drag and Drop Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-12 text-center transition-all ${
                  dragActive 
                    ? 'border-indigo-500 bg-indigo-50' 
                    : 'border-slate-300 bg-slate-50 hover:bg-slate-100'
                }`}
                data-testid="file-drop-zone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileChange}
                  className="hidden"
                  data-testid="file-input"
                />
                
                {file ? (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-green-100 rounded-xl flex items-center justify-center mx-auto">
                      <FileSpreadsheet className="w-8 h-8 text-green-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-slate-900">{file.name}</p>
                      <p className="text-sm text-slate-500 mt-1">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                    <Button 
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="change-file-btn"
                    >
                      Change File
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="w-16 h-16 bg-indigo-100 rounded-xl flex items-center justify-center mx-auto">
                      <Upload className="w-8 h-8 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-lg font-semibold text-slate-900 mb-2">Drop your CSV file here</p>
                      <p className="text-sm text-slate-500">or</p>
                    </div>
                    <Button 
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700"
                      data-testid="browse-file-btn"
                    >
                      Browse Files
                    </Button>
                  </div>
                )}
              </div>

              {/* Upload Button */}
              <Button
                onClick={handleUpload}
                disabled={!file || uploading}
                className="w-full h-12 bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-base"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Importing Leads...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Import & Enrich Leads
                  </>
                )}
              </Button>

              {/* Import Result */}
              {result && (
                <div className="space-y-4 p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200" data-testid="import-result">
                  <div className="flex items-center space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                    <h3 className="text-lg font-semibold text-green-900">Import Successful!</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white/70 p-4 rounded-lg">
                      <p className="text-sm text-slate-600 font-medium">Imported</p>
                      <p className="text-2xl font-bold text-green-600 mt-1">{result.imported}</p>
                    </div>
                    <div className="bg-white/70 p-4 rounded-lg">
                      <p className="text-sm text-slate-600 font-medium">Errors</p>
                      <p className="text-2xl font-bold text-slate-900 mt-1">{result.errors}</p>
                    </div>
                  </div>

                  {result.errors > 0 && result.error_details && (
                    <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                      <div className="flex items-center space-x-2 mb-2">
                        <AlertCircle className="w-4 h-4 text-amber-600" />
                        <p className="text-sm font-semibold text-amber-900">Import Errors:</p>
                      </div>
                      <ul className="text-sm text-amber-800 space-y-1">
                        {result.error_details.map((err, idx) => (
                          <li key={idx}>Row {err.row}: {err.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex items-center space-x-2 text-sm text-slate-600 bg-white/70 p-4 rounded-lg">
                    <Sparkles className="w-4 h-4 text-indigo-600" />
                    <span>AI enrichment is running in the background. Refresh the leads page to see updates.</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Instructions */}
        <div className="space-y-6">
          <Card className="glass-card border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">CSV Template</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-600">
                Download our template to ensure your CSV is formatted correctly.
              </p>
              <Button 
                variant="outline" 
                onClick={downloadTemplate}
                className="w-full"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </CardContent>
          </Card>

          <Card className="glass-card border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="text-lg">Required Columns</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>name</strong> - Lead's full name (required)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>email</strong> - Email address</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>phone</strong> - Phone number</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>company</strong> - Company name</span>
                </li>
                <li className="flex items-start space-x-2">
                  <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  <span><strong>title</strong> - Job title</span>
                </li>
              </ul>
            </CardContent>
          </Card>

          <Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-indigo-50 to-blue-50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center space-x-2">
                <Sparkles className="w-5 h-5 text-indigo-600" />
                <span>AI Enrichment</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-700">
                Our AI will automatically enrich your leads with:
              </p>
              <ul className="mt-3 space-y-2 text-sm text-slate-600">
                <li>• Company size & industry</li>
                <li>• Revenue estimates</li>
                <li>• LinkedIn profiles</li>
                <li>• Website & location</li>
                <li>• Lead scoring</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>
      
      {/* Usage Limit Modal */}
      <LeadLimitModal
        isOpen={modals.leadLimit.isOpen}
        onClose={() => closeModal('leadLimit')}
        limitDetails={modals.leadLimit.details}
      />
    </div>
  );
};

export default ImportLeads;
